"use client";

import {
  useCallback,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import { Mail } from "lucide-react";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
  SheetTrigger,
} from "@/app/components/ui/sheet";
import { ContactFormVariant } from "@/app/components/forms/contact/contact-form-variant";
import { useMediaQuery } from "@/app/hooks/gallery-lightbox/use-media-query";
import { cn } from "@/lib/utils";
import { Button, type ButtonProps } from "@/app/components/ui/button";
import { usePublicPathname } from "@/app/hooks/use-public-pathname";
import type { ContactFormSelectOptionT } from "./contact-form-types";
import type {
  ContactFormVariantContextRequirementT,
  ContactFormVariantContextT,
  ContactFormVariantFormDataT,
  ContactFormVariantKeyT,
  ContactFormVariantSelectFieldNameT,
} from "@/app/components/forms/contact/contact-form-variant-registry";
import type { DefaultValues } from "react-hook-form";

type SheetSideT = "left" | "right";
type SheetSizeT =
  "default" | "sm" | "md" | "lg" | "xl" | "2xl" | "3xl" | "full";

type ContactFormSheetBaseProps = {
  trigger?: ReactNode;
  triggerLabel?: string;
  triggerVariant?: ButtonProps["variant"];
  triggerSize?: ButtonProps["size"];
  triggerClassName?: string;
  title?: string;
  description?: string;
  desktopSide?: SheetSideT;
  desktopSize?: SheetSizeT;
  headerClassName?: string;
  contentClassName?: string;
  formWrapperClassName?: string;
};

type ContactFormSheetContextProp<TVariant extends ContactFormVariantKeyT> =
  ContactFormVariantContextRequirementT<TVariant> extends true
    ? { variantContext: ContactFormVariantContextT<TVariant> }
    : { variantContext?: ContactFormVariantContextT<TVariant> };

type ContactFormSheetProps<TVariant extends ContactFormVariantKeyT> =
  ContactFormSheetContextProp<TVariant> &
    ContactFormSheetBaseProps & {
      variant: TVariant;
      runtimeSelectOptions?: Partial<
        Record<
          ContactFormVariantSelectFieldNameT<TVariant>,
          ContactFormSelectOptionT[]
        >
      >;
      formPresentation?: "card" | "plain";
      showFormHeader?: boolean;
      formClassName?: string;
      headerContent?: ReactNode;
      lockedSelectValues?: Partial<
        Record<ContactFormVariantSelectFieldNameT<TVariant>, string>
      >;
      defaultValueOverrides?: DefaultValues<
        ContactFormVariantFormDataT<TVariant>
      >;
      submitOptions?: {
        skipWebhook?: boolean;
      };
    };

/**
 * Reusable sheet wrapper for the offer contact form.
 * Provides responsive behaviour (right-aligned on desktop, bottom sheet on mobile)
 * and exposes styling hooks for triggers, headers, and content.
 */
export default function ContactFormSheet<
  TVariant extends ContactFormVariantKeyT,
>({
  variant,
  variantContext,
  runtimeSelectOptions,
  formPresentation = "plain",
  showFormHeader = false,
  formClassName,
  trigger,
  triggerLabel = "Kontaktformular",
  triggerVariant = "default",
  triggerSize = "default",
  triggerClassName,
  title = "Kontaktformular",
  description,
  desktopSide = "right",
  desktopSize = "xl",
  headerClassName,
  contentClassName,
  formWrapperClassName,
  headerContent,
  lockedSelectValues,
  defaultValueOverrides,
  submitOptions,
}: ContactFormSheetProps<TVariant>) {
  const pathname = usePublicPathname();
  const isSkoda = pathname?.startsWith("/skoda");
  const [isMounted, setIsMounted] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const isMobile = useMediaQuery("(max-width: 767px)");

  // Avoid SSR/CSR id-Mismatches (Radix Dialog ids) by hydrating first
  useEffect(() => setIsMounted(true), []);

  const handleOpenChange = useCallback((nextOpen: boolean) => {
    setIsOpen(nextOpen);
  }, []);

  const sheetConfig = useMemo(() => {
    if (isMobile) {
      return {
        side: "bottom" as const,
        size: "full" as SheetSizeT,
        contentClasses:
          "h-[85vh] max-h-[85vh] overflow-y-auto rounded-t-lg p-0",
        headerClasses: "px-5 pt-6 pb-4",
        formWrapperClasses: "px-5 py-6",
      };
    }

    return {
      side: desktopSide,
      size: desktopSize,
      contentClasses: "overflow-y-auto px-0 sm:px-0",
      headerClasses: "px-6 pb-4",
      formWrapperClasses: "px-6 py-6",
    };
  }, [desktopSide, desktopSize, isMobile]);

  if (!isMounted) {
    // Render a stable, non-interactive placeholder for SSR/hydration
    return (
      trigger ?? (
        <Button
          variant={triggerVariant}
          size={triggerSize}
          className={cn(triggerClassName)}
          aria-hidden="true"
          tabIndex={-1}
        >
          {triggerLabel}
        </Button>
      )
    );
  }

  return (
    <Sheet open={isOpen} onOpenChange={handleOpenChange}>
      <SheetTrigger asChild>
        {trigger ?? (
          <Button
            variant={triggerVariant}
            size={triggerSize}
            className={cn(triggerClassName)}
          >
            {triggerLabel}
          </Button>
        )}
      </SheetTrigger>
      <SheetContent
        side={sheetConfig.side}
        size={sheetConfig.size}
        className={cn(sheetConfig.contentClasses, contentClassName)}
        data-brand={isSkoda ? "skoda" : undefined}
      >
        <SheetHeader
          className={cn(sheetConfig.headerClasses, "pb-0", headerClassName)}
        >
          <SheetTitle className="sr-only">{title}</SheetTitle>
          {description && (
            <SheetDescription className="sr-only">
              {description}
            </SheetDescription>
          )}
          <div className="flex justify-start">
            {headerContent ?? (
              <div className="bg-light-blue flex h-12 w-12 items-center justify-center rounded-full">
                <Mail className="text-primary h-6 w-6" />
              </div>
            )}
          </div>
        </SheetHeader>
        <div
          className={cn(
            sheetConfig.formWrapperClasses,
            "pt-8",
            formWrapperClassName
          )}
        >
          <ContactFormVariant
            variant={variant}
            variantContext={variantContext}
            runtimeSelectOptions={runtimeSelectOptions}
            presentation={formPresentation}
            showHeader={showFormHeader}
            lockedSelectValues={lockedSelectValues}
            defaultValueOverrides={defaultValueOverrides}
            className={formClassName}
            submitOptions={submitOptions}
          />
        </div>
      </SheetContent>
    </Sheet>
  );
}
